import { format, isToday, isTomorrow, eachDayOfInterval } from 'date-fns';
import { MapPin } from 'lucide-react';
import type { CalendarEvent, Member } from '../types/calendar';
import { isEventPast } from '../lib/event-utils';
import { deduplicateEvents } from '../lib/event-deduplication';
import { MemberDotsStack } from './calendar/MemberDotsStack';
import { useEffect, useRef } from 'react';
import { useSwipe } from '../hooks/useSwipe';

interface AgendaMobileViewProps {
  date: Date;
  events: Record<string, CalendarEvent[]> | Record<string, Record<string, CalendarEvent[]>>; // Single day events or multi-day events
  members: Member[];
  formatTime: (date: Date | string) => string;
  onEventClick: (event: CalendarEvent, member?: Member) => void;
  dateRange?: { start: Date; end: Date }; // Optional date range for week view
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

/**
 * Mobile-optimized agenda view with time-grouped event cards.
 * Designed for phone screens (<480px) with sticky day headers,
 * member-colored borders, and a "now" indicator for today's events.
 */
export function AgendaMobileView({
  date,
  events,
  members,
  formatTime,
  onEventClick,
  dateRange,
  onSwipeLeft,
  onSwipeRight,
}: AgendaMobileViewProps) {
  const nowIndicatorRef = useRef<HTMLDivElement>(null);
  const swipeRef = useSwipe({
    onSwipeLeft,
    onSwipeRight,
  });
  
  // Check if this is a multi-day view (week mode)
  const isMultiDay = dateRange != null;
  
  // Get all dates to show
  const datesToShow = isMultiDay 
    ? eachDayOfInterval(dateRange!)
    : [date];

  // Auto-scroll to "now" indicator on mount
  useEffect(() => {
    if (nowIndicatorRef.current) {
      setTimeout(() => {
        nowIndicatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, []);

  /**
   * Get deduplicated events for a specific date.
   */
  function getEventsForDate(currentDate: Date) {
    const dateKey = format(currentDate, 'yyyy-MM-dd');
    
    // Get events for this specific date
    const dayEvents = isMultiDay 
      ? (events as Record<string, Record<string, CalendarEvent[]>>)[dateKey] || {}
      : events as Record<string, CalendarEvent[]>;

    // Deduplicate events to combine shared events
    return deduplicateEvents(dayEvents, members);
  }

  /**
   * Generate border style for event cards.
   * Single-member: solid color border. Multi-member: gradient border.
   */
  function getEventBorderStyle(eventMembers: Member[]): React.CSSProperties {
    if (eventMembers.length <= 1) {
      return {
        borderLeft: `4px solid ${eventMembers[0]?.color || '#ccc'}`,
      };
    }
    // Equal vertical segments for each member color
    const stops = eventMembers
      .map((m, i) => {
        const start = (i / eventMembers.length) * 100;
        const end = ((i + 1) / eventMembers.length) * 100;
        return `${m.color} ${start}% ${end}%`;
      })
      .join(', ');
    return {
      borderLeft: '4px solid transparent',
      borderImage: `linear-gradient(to bottom, ${stops}) 1`,
    };
  }

  return (
    <div ref={swipeRef} className="flex flex-col h-full bg-bg-primary overflow-auto">
      {datesToShow.map((currentDate) => {
        const dateKey = format(currentDate, 'yyyy-MM-dd');
        const deduplicatedEvents = getEventsForDate(currentDate);
        
        // Separate all-day and timed events
        const allDayEvents = deduplicatedEvents.filter((e) => e.allDay);
        const timedEvents = deduplicatedEvents
          .filter((e) => !e.allDay)
          .sort((a, b) => 
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
          );

        // Check if today
        const dayIsToday = isToday(currentDate);
        const dayIsTomorrow = isTomorrow(currentDate);

        // Split timed events into past and upcoming for "now" indicator
        const pastEvents = dayIsToday 
          ? timedEvents.filter((e) => isEventPast(e))
          : [];
        const upcomingEvents = dayIsToday
          ? timedEvents.filter((e) => !isEventPast(e))
          : timedEvents;

        const hasNoEvents = allDayEvents.length === 0 && timedEvents.length === 0;

        return (
          <div key={dateKey}>
            {/* Sticky Day Header */}
            <div className="sticky top-0 z-10 bg-bg-primary border-b border-border-default px-4 py-2">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-text-primary">
                  {format(currentDate, 'EEEE, MMMM d')}
                </h3>
                {dayIsToday && (
                  <span className="px-2 py-0.5 bg-teal/15 text-teal text-xs font-semibold rounded-full">
                    TODAY
                  </span>
                )}
                {dayIsTomorrow && (
                  <span className="px-2 py-0.5 bg-amber/15 text-amber text-xs font-semibold rounded-full">
                    TOMORROW
                  </span>
                )}
              </div>
            </div>

            {/* Empty state */}
            {hasNoEvents && (
              <div className="px-4 py-6 text-center text-text-muted text-sm">
                No events
              </div>
            )}

            {/* All-day events pills */}
            {allDayEvents.length > 0 && (
              <div className="overflow-x-auto whitespace-nowrap flex gap-2 px-4 py-3 border-b border-border-light">
                {allDayEvents.map((event) => (
                  <button
                    key={`${event.id}-allday`}
                    onClick={() => onEventClick(event, event.member ?? undefined)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-opacity hover:opacity-80"
                    style={{
                      backgroundColor: `color-mix(in srgb, ${event.members[0]?.color || '#ccc'} 15%, white 85%)`,
                      color: event.members[0]?.color || '#666',
                    }}
                  >
                    {event.equipmentFlags && event.equipmentFlags.length > 0 && (
                      <span>🎒</span>
                    )}
                    <span className="truncate max-w-[200px]">{event.title}</span>
                    {event.isShared && (
                      <MemberDotsStack members={event.members} size="sm" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Past timed events */}
            {pastEvents.length > 0 && (
              <div className="px-4 py-2 space-y-2">
                {pastEvents.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => onEventClick(event, event.member ?? undefined)}
                    className="w-full bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow min-h-[44px] opacity-60"
                    style={getEventBorderStyle(event.members)}
                  >
                    <div className="px-3 py-2.5 text-left">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text-secondary">
                            {formatTime(event.startTime)} - {formatTime(event.endTime)}
                          </span>
                          {event.isShared && (
                            <MemberDotsStack members={event.members} size="sm" />
                          )}
                        </div>
                      </div>
                      <div className="text-base font-medium text-text-primary truncate">
                        {event.equipmentFlags && event.equipmentFlags.length > 0 && (
                          <span className="mr-1">🎒</span>
                        )}
                        {event.title}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-text-muted">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* "Now" indicator */}
            {dayIsToday && timedEvents.length > 0 && pastEvents.length > 0 && upcomingEvents.length > 0 && (
              <div 
                ref={nowIndicatorRef}
                className="flex items-center gap-2 px-4 py-2"
              >
                <div className="w-2 h-2 rounded-full bg-coral flex-shrink-0" />
                <div className="flex-1 border-t-2 border-coral" />
                <span className="text-xs font-semibold text-coral uppercase tracking-wide">
                  now
                </span>
              </div>
            )}

            {/* Upcoming timed events */}
            {upcomingEvents.length > 0 && (
              <div className="px-4 py-2 space-y-2">
                {upcomingEvents.map((event) => (
                  <button
                    key={event.id}
                    onClick={() => onEventClick(event, event.member ?? undefined)}
                    className="w-full bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow min-h-[44px]"
                    style={getEventBorderStyle(event.members)}
                  >
                    <div className="px-3 py-2.5 text-left">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-text-secondary">
                            {formatTime(event.startTime)} - {formatTime(event.endTime)}
                          </span>
                          {event.isShared && (
                            <MemberDotsStack members={event.members} size="sm" />
                          )}
                        </div>
                      </div>
                      <div className="text-base font-medium text-text-primary truncate">
                        {event.equipmentFlags && event.equipmentFlags.length > 0 && (
                          <span className="mr-1">🎒</span>
                        )}
                        {event.title}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-text-muted">
                          <MapPin className="w-3 h-3" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* "No more events today" message for days with only past events */}
            {dayIsToday && upcomingEvents.length === 0 && timedEvents.length > 0 && (
              <div className="px-4 py-4 text-center text-text-muted text-sm">
                No more events today
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
